function _classPrivateFieldLooseBase(e, t) { if (!{}.hasOwnProperty.call(e, t)) throw new TypeError("attempted to use private field on non-instance"); return e; }
var id = 0;
function _classPrivateFieldLooseKey(e) { return "__private_" + id++ + "_" + e; }
import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';
import { DiscountUpdateEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';

/**
 * A custom element that applies a discount to the cart.
 *
 * @typedef {Object} CartDiscountComponentRefs
 * @property {HTMLElement} cartDiscountError - The error element.
 * @property {HTMLElement} cartDiscountErrorDiscountCode - The discount code error element.
 * @property {HTMLElement} cartDiscountErrorShipping - The shipping error element.
 */

/**
 * @extends {Component<CartDiscountComponentRefs>}
 */
var _activeFetch = _classPrivateFieldLooseKey("activeFetch");
var _createAbortController = _classPrivateFieldLooseKey("createAbortController");
var _handleDiscountError = _classPrivateFieldLooseKey("handleDiscountError");
var _existingDiscounts = _classPrivateFieldLooseKey("existingDiscounts");
class CartDiscount extends Component {
  constructor(...args) {
    super(...args);
    /**
     * Returns an array of existing discount codes.
     * @returns {string[]}
     */
    Object.defineProperty(this, _existingDiscounts, {
      value: _existingDiscounts2
    });
    /**
     * Handles the discount error.
     *
     * @param {'discount_code' | 'shipping'} type - The type of discount error.
     */
    Object.defineProperty(this, _handleDiscountError, {
      value: _handleDiscountError2
    });
    Object.defineProperty(this, _createAbortController, {
      value: _createAbortController2
    });
    this.requiredRefs = ['cartDiscountError', 'cartDiscountErrorDiscountCode', 'cartDiscountErrorShipping'];
    /** @type {AbortController | null} */
    Object.defineProperty(this, _activeFetch, {
      writable: true,
      value: null
    });
    /**
     * Handles updates to the cart note.
     * @param {SubmitEvent} event - The submit event on our form.
     */
    this.applyDiscount = async event => {
      const {
        cartDiscountError,
        cartDiscountErrorDiscountCode,
        cartDiscountErrorShipping
      } = this.refs;
      event.preventDefault();
      event.stopPropagation();
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      const discountCode = form.querySelector('input[name="discount"]');
      if (!(discountCode instanceof HTMLInputElement) || typeof this.dataset.sectionId !== 'string') return;
      const discountCodeValue = discountCode.value;
      const abortController = _classPrivateFieldLooseBase(this, _createAbortController)[_createAbortController]();
      try {
        const existingDiscounts = _classPrivateFieldLooseBase(this, _existingDiscounts)[_existingDiscounts]();
        if (existingDiscounts.includes(discountCodeValue)) return;
        cartDiscountError.classList.add('hidden');
        cartDiscountErrorDiscountCode.classList.add('hidden');
        cartDiscountErrorShipping.classList.add('hidden');
        const config = fetchConfig('json', {
          body: JSON.stringify({
            discount: [...existingDiscounts, discountCodeValue].join(','),
            sections: [this.dataset.sectionId]
          })
        });
        const response = await fetch(Theme.routes.cart_update_url, {
          ...config,
          signal: abortController.signal
        });
        const data = await response.json();
        if (data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */discount) => {
          return discount.code === discountCodeValue && discount.applicable === false;
        })) {
          discountCode.value = '';
          _classPrivateFieldLooseBase(this, _handleDiscountError)[_handleDiscountError]('discount_code');
          return;
        }
        const newHtml = data.sections[this.dataset.sectionId];
        const parsedHtml = new DOMParser().parseFromString(newHtml, 'text/html');
        const section = parsedHtml.getElementById(`shopify-section-${this.dataset.sectionId}`);
        const discountCodes = section?.querySelectorAll('.cart-discount__pill') || [];
        if (section) {
          const codes = Array.from(discountCodes).map(element => element instanceof HTMLLIElement ? element.dataset.discountCode : null).filter(Boolean);
          // Before morphing, we need to check if the shipping discount is applicable in the UI
          // we check the liquid logic compared to the cart payload to assess whether we leveraged
          // a valid shipping discount code.
          if (codes.length === existingDiscounts.length && codes.every((/** @type {string} */code) => existingDiscounts.includes(code)) && data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */discount) => {
            return discount.code === discountCodeValue && discount.applicable === true;
          })) {
            _classPrivateFieldLooseBase(this, _handleDiscountError)[_handleDiscountError]('shipping');
            discountCode.value = '';
            return;
          }
        }
        document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
        morphSection(this.dataset.sectionId, newHtml);
      } catch (error) {} finally {
        _classPrivateFieldLooseBase(this, _activeFetch)[_activeFetch] = null;
        cartPerformance.measureFromEvent('discount-update:user-action', event);
      }
    };
    /**
     * Handles removing a discount from the cart.
     * @param {MouseEvent | KeyboardEvent} event - The mouse or keyboard event in our pill.
     */
    this.removeDiscount = async event => {
      event.preventDefault();
      event.stopPropagation();
      if (event instanceof KeyboardEvent && event.key !== 'Enter' || !(event instanceof MouseEvent) || !(event.target instanceof HTMLElement) || typeof this.dataset.sectionId !== 'string') {
        return;
      }
      const pill = event.target.closest('.cart-discount__pill');
      if (!(pill instanceof HTMLLIElement)) return;
      const discountCode = pill.dataset.discountCode;
      if (!discountCode) return;
      const existingDiscounts = _classPrivateFieldLooseBase(this, _existingDiscounts)[_existingDiscounts]();
      const index = existingDiscounts.indexOf(discountCode);
      if (index === -1) return;
      existingDiscounts.splice(index, 1);
      const abortController = _classPrivateFieldLooseBase(this, _createAbortController)[_createAbortController]();
      try {
        const config = fetchConfig('json', {
          body: JSON.stringify({
            discount: existingDiscounts.join(','),
            sections: [this.dataset.sectionId]
          })
        });
        const response = await fetch(Theme.routes.cart_update_url, {
          ...config,
          signal: abortController.signal
        });
        const data = await response.json();
        document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
        morphSection(this.dataset.sectionId, data.sections[this.dataset.sectionId]);
      } catch (error) {} finally {
        _classPrivateFieldLooseBase(this, _activeFetch)[_activeFetch] = null;
      }
    };
  }
}
function _createAbortController2() {
  if (_classPrivateFieldLooseBase(this, _activeFetch)[_activeFetch]) {
    _classPrivateFieldLooseBase(this, _activeFetch)[_activeFetch].abort();
  }
  const abortController = new AbortController();
  _classPrivateFieldLooseBase(this, _activeFetch)[_activeFetch] = abortController;
  return abortController;
}
function _handleDiscountError2(type) {
  const {
    cartDiscountError,
    cartDiscountErrorDiscountCode,
    cartDiscountErrorShipping
  } = this.refs;
  const target = type === 'discount_code' ? cartDiscountErrorDiscountCode : cartDiscountErrorShipping;
  cartDiscountError.classList.remove('hidden');
  target.classList.remove('hidden');
}
function _existingDiscounts2() {
  /** @type {string[]} */
  const discountCodes = [];
  const discountPills = this.querySelectorAll('.cart-discount__pill');
  for (const pill of discountPills) {
    if (pill instanceof HTMLLIElement && typeof pill.dataset.discountCode === 'string') {
      discountCodes.push(pill.dataset.discountCode);
    }
  }
  return discountCodes;
}
if (!customElements.get('cart-discount-component')) {
  customElements.define('cart-discount-component', CartDiscount);
}
